import { HTTPClientInterface, HTTPRequest } from '@shared/core/HTTPClientInterface';
import { buildURL, SystemURL } from './URLCatalog';

export namespace RosterServiceModel {
    export type RosterStatus =
        | 'PRESENTE'
        | 'CON_DEMORA'
        | 'SIN_PRESENTARSE'
        | 'AUSENTE'
        | 'PLANIFICADO';

    export interface RosterSummary {
        total: number;
        presente: number;
        conDemora: number;
        sinPresentarse: number;
        ausente: number;
        planificado: number;
    }

    export interface RosterRow {
        userID: string;
        userName: string;
        userImage: string;
        shiftInstanceID: string;
        buildingName: string;
        hourFrom: string;
        hourTo: string;
        timestampFrom: number;
        actualCheckinTimestamp: number | null;
        status: RosterStatus;
    }

    export interface RosterResponse {
        summary: RosterSummary;
        rows: RosterRow[];
    }

    export type QontactPointShiftMode = 'assigned' | 'count';

    export interface QontactPointShiftUser {
        userID: string;
        userName: string;
        userImage: string;
        actualCheckinTimestamp: number | null;
        actualCheckoutTimestamp: number | null;
        status: RosterStatus;
    }

    export interface QontactPointShiftEntry {
        shiftInstanceID: string;
        recipeID: string;
        name: string;
        hourFrom: string;
        hourTo: string;
        timestampFrom: number;
        timestampTo: number;
        expected: number;
        // 'assigned': users are the people assigned to the shift (present or not).
        // 'count': users are the people who actually checked in to the shift.
        mode: QontactPointShiftMode;
        checkedInCount: number;
        users: QontactPointShiftUser[];
    }

    export interface QontactPointRosterEntry {
        qontactPointID: string;
        buildingID: string;
        buildingName: string;
        shifts: QontactPointShiftEntry[];
    }

    export interface QontactPointRosterResponse {
        qontactPoints: QontactPointRosterEntry[];
    }

    export class Parser {
        static parseRow(raw: any): RosterRow {
            return {
                userID: raw?.userID ?? '',
                userName: raw?.userName ?? '',
                userImage: raw?.userImage ?? '',
                shiftInstanceID: raw?.shiftInstanceID ?? '',
                buildingName: raw?.buildingName ?? '',
                hourFrom: raw?.hourFrom ?? '',
                hourTo: raw?.hourTo ?? '',
                timestampFrom: raw?.timestampFrom ?? 0,
                actualCheckinTimestamp: raw?.actualCheckinTimestamp ?? null,
                status: raw?.status ?? 'PLANIFICADO',
            };
        }

        static parse(raw: any): RosterResponse {
            return {
                summary: {
                    total: raw?.summary?.total ?? 0,
                    presente: raw?.summary?.presente ?? 0,
                    conDemora: raw?.summary?.conDemora ?? 0,
                    sinPresentarse: raw?.summary?.sinPresentarse ?? 0,
                    ausente: raw?.summary?.ausente ?? 0,
                    planificado: raw?.summary?.planificado ?? 0,
                },
                rows: Array.isArray(raw?.rows) ? raw.rows.map(Parser.parseRow) : [],
            };
        }

        static parseQontactPointShiftUser(raw: any): QontactPointShiftUser {
            return {
                userID: raw?.userID ?? '',
                userName: raw?.userName ?? '',
                userImage: raw?.userImage ?? '',
                actualCheckinTimestamp: raw?.actualCheckinTimestamp ?? null,
                actualCheckoutTimestamp: raw?.actualCheckoutTimestamp ?? null,
                status: raw?.status ?? 'PLANIFICADO',
            };
        }

        static parseQontactPointShift(raw: any): QontactPointShiftEntry {
            return {
                shiftInstanceID: raw?.shiftInstanceID ?? '',
                recipeID: raw?.recipeID ?? '',
                name: raw?.name ?? '',
                hourFrom: raw?.hourFrom ?? '',
                hourTo: raw?.hourTo ?? '',
                timestampFrom: raw?.timestampFrom ?? 0,
                timestampTo: raw?.timestampTo ?? 0,
                expected: raw?.expected ?? 0,
                mode: raw?.mode === 'count' ? 'count' : 'assigned',
                checkedInCount: raw?.checkedInCount ?? 0,
                users: Array.isArray(raw?.users)
                    ? raw.users.map(Parser.parseQontactPointShiftUser)
                    : [],
            };
        }

        static parseQontactPointEntry(raw: any): QontactPointRosterEntry {
            return {
                qontactPointID: raw?.qontactPointID ?? '',
                buildingID: raw?.buildingID ?? '',
                buildingName: raw?.buildingName ?? '',
                shifts: Array.isArray(raw?.shifts)
                    ? raw.shifts.map(Parser.parseQontactPointShift)
                    : [],
            };
        }

        static parseQontactPointRoster(raw: any): QontactPointRosterResponse {
            return {
                qontactPoints: Array.isArray(raw?.qontactPoints)
                    ? raw.qontactPoints.map(Parser.parseQontactPointEntry)
                    : [],
            };
        }
    }
}

export class RosterService {
    private httpClient: HTTPClientInterface;

    constructor(httpClient: HTTPClientInterface) {
        this.httpClient = httpClient;
    }

    async getRosterForDay(
        orgID: string,
        day: string,
    ): Promise<RosterServiceModel.RosterResponse> {
        const request = new HTTPRequest();
        request.url = buildURL(SystemURL.Roster.getRosterForDay, {
            org_id: orgID,
            day,
        });
        request.method = 'GET';
        const response = await this.httpClient.execute(request);
        return RosterServiceModel.Parser.parse(response.result);
    }

    async getRosterByQontactPointForDay(
        orgID: string,
        day: string,
    ): Promise<RosterServiceModel.QontactPointRosterResponse> {
        const request = new HTTPRequest();
        request.url = buildURL(SystemURL.Roster.getRosterByQontactPointForDay, {
            org_id: orgID,
            day,
        });
        request.method = 'GET';
        const response = await this.httpClient.execute(request);
        return RosterServiceModel.Parser.parseQontactPointRoster(response.result);
    }
}
